/**
 * Shared amenity form used by both CreateAmenityDialog and EditAmenityDialog.
 * Split into three flat sections rendered as vertical tabs so the dialog
 * doesn't become one very long scroll.
 */
import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/system/Unstable_Grid';
import {
  CalendarDots as CalendarDotsIcon,
  CurrencyDollar as CurrencyDollarIcon,
  GearSix as GearSixIcon,
  Info as InfoIcon,
  Minus as MinusIcon,
  Plus as PlusIcon,
  TextAlignLeft as TextAlignLeftIcon,
} from '@phosphor-icons/react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import type { Control, FieldErrors } from 'react-hook-form';
import dayjs from 'dayjs';

import { MAX_AMENITY_DEPOSIT_AMOUNT, MAX_AMENITY_TOTAL_FEE_AMOUNT } from './amenity-payment-limits';

export const DAYS_OF_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
export type DayOfWeek = (typeof DAYS_OF_WEEK)[number];

const DAY_LABEL: Record<DayOfWeek, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
};
const DAY_LABEL_FULL: Record<DayOfWeek, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

export const DEFAULT_START = '08:00';
export const DEFAULT_END = '22:00';

export interface AmenityFormValues {
  name: string;
  description?: string;
  rules?: string;
  fee_description?: string;
  deposit_amount?: number;
  total_fee_amount?: number;
  required_documents: string[];
  availability: Record<DayOfWeek, { enabled: boolean; start: string; end: string }>;
  seasonal_enabled: boolean;
  seasonal_start_date: string | null;
  seasonal_end_date: string | null;
  time_block_duration: number | null;
  reservation_min_duration: number | null;
  reservation_max_duration: number | null;
  max_guests?: number | null;
  guest_count_required: boolean;
  alcohol_question_enabled: boolean;
  requires_approval?: boolean;
  is_active?: boolean;
}

interface Props {
  control: Control<AmenityFormValues>;
  errors: FieldErrors<AmenityFormValues>;
  mode: 'create' | 'edit';
  paymentsEnabled?: boolean;
  /** Live Stripe Connect readiness (connected + onboarding complete + charges enabled). */
  stripeReady?: boolean;
}

const sectionSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  overflow: 'hidden',
} as const;

const sectionHeaderSx = {
  px: 2.5,
  py: 1.5,
  borderBottom: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.default',
} as const;

const sectionBodySx = { p: 2.5 } as const;

export function AmenityForm({ control, errors, mode, paymentsEnabled = true, stripeReady = true }: Props): React.JSX.Element {
  const [tab, setTab] = React.useState(0);

  const seasonalEnabled = useWatch({ control, name: 'seasonal_enabled' });
  const isActive = useWatch({ control, name: 'is_active' });
  const requiresApproval = useWatch({ control, name: 'requires_approval' });
  const depositAmount = useWatch({ control, name: 'deposit_amount' });
  const totalFeeAmount = useWatch({ control, name: 'total_fee_amount' });
  const timeBlockDuration = useWatch({ control, name: 'time_block_duration' });
  const hasDeposit = Number(depositAmount) > 0;
  const hasCharge = hasDeposit || Number(totalFeeAmount) > 0;
  const hasTimeBlock = timeBlockDuration != null && Number(timeBlockDuration) > 0;
  const maxTotalFeeLabel = MAX_AMENITY_TOTAL_FEE_AMOUNT.toLocaleString();
  const maxDepositLabel = MAX_AMENITY_DEPOSIT_AMOUNT.toLocaleString();

  const remainingFee =
    totalFeeAmount != null && depositAmount != null
      ? Math.max(0, Number(totalFeeAmount) - Number(depositAmount))
      : totalFeeAmount != null
        ? Number(totalFeeAmount)
        : null;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'required_documents',
  });

  return (
    <Box>
      {/* Tab bar */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 3,
          '& .MuiTabs-flexContainer': { gap: 0.5 },
          '& .MuiTab-root': {
            minHeight: 36,
            py: 0.75,
            px: 1.5,
            fontSize: '0.8125rem',
            fontWeight: 500,
            textTransform: 'none',
            borderRadius: 1.5,
            '&.Mui-selected': { fontWeight: 600 },
          },
          '& .MuiTabs-indicator': { borderRadius: 2 },
        }}
      >
        <Tab label="Overview" icon={<TextAlignLeftIcon size={15} />} iconPosition="start" />
        <Tab label="Availability" icon={<CalendarDotsIcon size={15} />} iconPosition="start" />
        <Tab label="Fees & Rules" icon={<CurrencyDollarIcon size={15} />} iconPosition="start" />
        {mode === 'edit' && (
          <Tab label="Settings" icon={<GearSixIcon size={15} />} iconPosition="start" />
        )}
      </Tabs>

      {/* ─── Overview ─── */}
      {tab === 0 && (
        <Stack spacing={3}>
          <Box sx={sectionSx}>
            <Box sx={sectionHeaderSx}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>General</Typography>
              <Typography variant="caption" color="text.secondary">Name and description for residents</Typography>
            </Box>
            <Box sx={sectionBodySx}>
              <Stack spacing={2.5}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Amenity Name"
                      required
                      fullWidth
                      size="small"
                      error={Boolean(errors.name)}
                      helperText={errors.name?.message ?? 'e.g. Clubhouse, Pool, Tennis Court'}
                      autoFocus={mode === 'create'}
                    />
                  )}
                />
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description"
                      fullWidth
                      multiline
                      rows={3}
                      size="small"
                      placeholder="Describe the amenity for residents making reservations…"
                      error={Boolean(errors.description)}
                      helperText={errors.description?.message}
                    />
                  )}
                />
              </Stack>
            </Box>
          </Box>

          <Box sx={sectionSx}>
            <Box sx={sectionHeaderSx}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Rules & Policies</Typography>
              <Typography variant="caption" color="text.secondary">Displayed to residents before booking</Typography>
            </Box>
            <Box sx={sectionBodySx}>
              <Controller
                name="rules"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Rules & Policies"
                    fullWidth
                    multiline
                    rows={4}
                    size="small"
                    placeholder="List any rules, noise restrictions, cleanup requirements, etc."
                    error={Boolean(errors.rules)}
                    helperText={errors.rules?.message}
                  />
                )}
              />
            </Box>
          </Box>

          {mode === 'create' && (
            <Box sx={sectionSx}>
              <Box sx={sectionHeaderSx}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Status</Typography>
              </Box>
              <Box sx={sectionBodySx}>
                <Stack direction="row" spacing={3}>
                  <Controller
                    name="requires_approval"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <FormControlLabel
                        control={<Switch size="small" checked={value ?? true} onChange={(e) => onChange(e.target.checked)} />}
                        label={<Stack spacing={0}><Typography variant="body2" sx={{ fontWeight: 500 }}>Requires Approval</Typography><Typography variant="caption" color="text.secondary">Manager must approve each booking</Typography></Stack>}
                      />
                    )}
                  />
                  <Controller
                    name="is_active"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <FormControlLabel
                        control={<Switch size="small" checked={value ?? true} onChange={(e) => onChange(e.target.checked)} />}
                        label={<Stack spacing={0}><Typography variant="body2" sx={{ fontWeight: 500 }}>Active</Typography><Typography variant="caption" color="text.secondary">Visible to residents</Typography></Stack>}
                      />
                    )}
                  />
                </Stack>
              </Box>
            </Box>
          )}
        </Stack>
      )}

      {/* ─── Availability ─── */}
      {tab === 1 && (
        <Stack spacing={3}>
          <Box sx={sectionSx}>
            <Box sx={sectionHeaderSx}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Weekly Schedule</Typography>
              <Typography variant="caption" color="text.secondary">Toggle each day and set available hours</Typography>
            </Box>
            <Box sx={sectionBodySx}>
              <Stack spacing={0.5}>
                {DAYS_OF_WEEK.map((day) => (
                  <Controller
                    key={day}
                    name={`availability.${day}`}
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          px: 1.5,
                          py: 1,
                          borderRadius: 1.5,
                          border: '1px solid',
                          borderColor: value.enabled ? 'primary.main' : 'divider',
                          bgcolor: value.enabled ? 'action.selected' : 'transparent',
                          transition: 'all 0.15s',
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={value.enabled}
                              onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontWeight: value.enabled ? 600 : 400, width: 36 }}>
                              {DAY_LABEL[day]}
                            </Typography>
                          }
                          sx={{ m: 0, minWidth: 100 }}
                        />
                        {value.enabled ? (
                          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flex: 1 }}>
                            <TimePicker
                              label="Open"
                              value={dayjs(value.start, 'HH:mm')}
                              onChange={(v) => onChange({ ...value, start: v ? v.format('HH:mm') : DEFAULT_START })}
                              slotProps={{ textField: { size: 'small', sx: { width: 130 } } }}
                            />
                            <Typography variant="caption" color="text.disabled">–</Typography>
                            <TimePicker
                              label="Close"
                              value={dayjs(value.end, 'HH:mm')}
                              onChange={(v) => onChange({ ...value, end: v ? v.format('HH:mm') : DEFAULT_END })}
                              slotProps={{ textField: { size: 'small', sx: { width: 130 } } }}
                            />
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.disabled">Closed</Typography>
                        )}
                      </Box>
                    )}
                  />
                ))}
              </Stack>
            </Box>
          </Box>

          <Box sx={sectionSx}>
            <Box sx={sectionHeaderSx}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Seasonal Availability</Typography>
              <Typography variant="caption" color="text.secondary">Restrict bookings to certain months of the year</Typography>
            </Box>
            <Box sx={sectionBodySx}>
              <Controller
                name="seasonal_enabled"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <FormControlLabel
                    control={<Switch size="small" checked={value} onChange={(e) => onChange(e.target.checked)} />}
                    label={<Typography variant="body2" sx={{ fontWeight: 500 }}>Enable seasonal restriction</Typography>}
                    sx={{ mb: seasonalEnabled ? 2 : 0 }}
                  />
                )}
              />
              {seasonalEnabled && (
                <Grid container spacing={2}>
                  <Grid xs={12} sm={6}>
                    <Controller
                      name="seasonal_start_date"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <DatePicker
                          label="Season Opens"
                          value={value ? dayjs(value) : null}
                          onChange={(v) => onChange(v ? v.format('YYYY-MM-DD') : null)}
                          slotProps={{ textField: { fullWidth: true, size: 'small', helperText: 'e.g. May 1' } }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid xs={12} sm={6}>
                    <Controller
                      name="seasonal_end_date"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <DatePicker
                          label="Season Closes"
                          value={value ? dayjs(value) : null}
                          onChange={(v) => onChange(v ? v.format('YYYY-MM-DD') : null)}
                          slotProps={{ textField: { fullWidth: true, size: 'small', helperText: 'e.g. October 1' } }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          </Box>

          <Box sx={sectionSx}>
            <Box sx={sectionHeaderSx}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Fixed Time Block</Typography>
              <Typography variant="caption" color="text.secondary">
                Lock every reservation to the same length — the day is split into back-to-back slots and residents just pick one
              </Typography>
            </Box>
            <Box sx={sectionBodySx}>
              <Stack spacing={1.5}>
                <Controller
                  name="time_block_duration"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      fullWidth
                      size="small"
                      label="Block length (hours)"
                      type="number"
                      value={value ? value / 60 : ''}
                      onChange={(e) => {
                        const hours = parseFloat(e.target.value);
                        onChange(hours > 0 ? Math.round(hours * 60) : null);
                      }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">hours</InputAdornment>,
                        inputProps: { min: 0, step: 0.5 },
                      }}
                      placeholder="e.g. 6"
                      helperText="Leave blank for flexible booking. Example: 6 = six-hour slots."
                    />
                  )}
                />
              </Stack>
            </Box>
          </Box>

          <Box sx={sectionSx}>
            <Box sx={sectionHeaderSx}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, opacity: hasTimeBlock ? 0.45 : 1 }}>
                Flexible Duration Limits
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ opacity: hasTimeBlock ? 0.45 : 1 }}>
                {hasTimeBlock
                  ? 'Disabled — a fixed time block is set above, so every booking is the same length'
                  : 'Let residents choose their own start & end times within these bounds'}
              </Typography>
            </Box>
            <Box sx={sectionBodySx}>
              <Stack spacing={2.5}>
                <Grid container spacing={2}>
                  <Grid xs={12} sm={6}>
                    <Controller
                      name="reservation_min_duration"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <FormControl fullWidth size="small" disabled={hasTimeBlock}>
                          <InputLabel>Minimum Duration</InputLabel>
                          <Select
                            value={value ?? ''}
                            label="Minimum Duration"
                            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
                          >
                            <MenuItem value="">No minimum</MenuItem>
                            <MenuItem value={60}>1 hour</MenuItem>
                            <MenuItem value={120}>2 hours</MenuItem>
                            <MenuItem value={180}>3 hours</MenuItem>
                            <MenuItem value={240}>4 hours</MenuItem>
                            <MenuItem value={360}>6 hours</MenuItem>
                            <MenuItem value={480}>8 hours</MenuItem>
                            <MenuItem value={720}>12 hours</MenuItem>
                            <MenuItem value={1440}>1 day (24 hours)</MenuItem>
                            <MenuItem value={2880}>2 days (48 hours)</MenuItem>
                            <MenuItem value={4320}>3 days</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid xs={12} sm={6}>
                    <Controller
                      name="reservation_max_duration"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <FormControl fullWidth size="small" disabled={hasTimeBlock}>
                          <InputLabel>Maximum Duration</InputLabel>
                          <Select
                            value={value ?? ''}
                            label="Maximum Duration"
                            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
                          >
                            <MenuItem value="">No maximum</MenuItem>
                            <MenuItem value={60}>1 hour</MenuItem>
                            <MenuItem value={120}>2 hours</MenuItem>
                            <MenuItem value={180}>3 hours</MenuItem>
                            <MenuItem value={240}>4 hours</MenuItem>
                            <MenuItem value={360}>6 hours</MenuItem>
                            <MenuItem value={480}>8 hours</MenuItem>
                            <MenuItem value={720}>12 hours</MenuItem>
                            <MenuItem value={1440}>1 day (24 hours)</MenuItem>
                            <MenuItem value={2880}>2 days (48 hours)</MenuItem>
                            <MenuItem value={4320}>3 days</MenuItem>
                            <MenuItem value={10080}>1 week</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>
                {!hasTimeBlock && (
                  <Typography variant="caption" color="text.secondary">
                    e.g. A pool may need no limits (just operating hours). A clubhouse might require a 4-hour minimum and 12-hour max.
                  </Typography>
                )}
              </Stack>
            </Box>
          </Box>
        </Stack>
      )}

      {/* ─── Fees & Rules ─── */}
      {tab === 2 && (
        <Stack spacing={3}>
          <Box sx={sectionSx}>
            <Box sx={sectionHeaderSx}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Pricing</Typography>
              <Typography variant="caption" color="text.secondary">Leave blank if the amenity is free</Typography>
            </Box>
            <Box sx={sectionBodySx}>
              <Stack spacing={2.5}>
                <Grid container spacing={2}>
                  <Grid xs={12} sm={6}>
                    <Controller
                      name="total_fee_amount"
                      control={control}
                      render={({ field: { value, onChange, ...field } }) => (
                        <TextField
                          {...field}
                          value={value ?? ''}
                          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
                          label="Total Fee"
                          type="number"
                          fullWidth
                          size="small"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            inputProps: { min: 0, max: MAX_AMENITY_TOTAL_FEE_AMOUNT, step: 0.01 },
                          }}
                          error={Boolean(errors.total_fee_amount)}
                          helperText={errors.total_fee_amount?.message ?? `Full reservation cost, up to $${maxTotalFeeLabel}`}
                        />
                      )}
                    />
                  </Grid>
                  <Grid xs={12} sm={6}>
                    <Controller
                      name="deposit_amount"
                      control={control}
                      render={({ field: { value, onChange, ...field } }) => (
                        <TextField
                          {...field}
                          value={value ?? ''}
                          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
                          label="Deposit Amount"
                          type="number"
                          fullWidth
                          size="small"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            inputProps: { min: 0, max: MAX_AMENITY_DEPOSIT_AMOUNT, step: 0.01 },
                          }}
                          error={Boolean(errors.deposit_amount)}
                          helperText={errors.deposit_amount?.message ?? `Collected upfront at booking, up to $${maxDepositLabel}`}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                {remainingFee !== null && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 1,
                      borderRadius: 1.5,
                      bgcolor: 'action.selected',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Tooltip title="Automatically calculated as Total Fee minus Deposit. Residents pay this remaining balance before their reservation date.">
                      <InfoIcon size={15} style={{ opacity: 0.5, cursor: 'help', flexShrink: 0 }} />
                    </Tooltip>
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                      Remaining fee due by resident
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      ${remainingFee.toFixed(2)}
                    </Typography>
                  </Box>
                )}
                {hasCharge && !paymentsEnabled && (
                  <Alert severity="warning" sx={{ mt: 0.5 }}>
                    Payments are currently disabled for this community. This fee will be saved but{' '}
                    <strong>won&apos;t be collected</strong> until an account administrator enables
                    payments in Account Settings.
                  </Alert>
                )}
                {hasCharge && paymentsEnabled && !stripeReady && (
                  <Alert severity="warning" sx={{ mt: 0.5 }}>
                    This community hasn&apos;t finished setting up online payments, so this fee{' '}
                    <strong>can&apos;t be collected</strong> yet. Connect a Stripe account under{' '}
                    <strong>Community Settings → Payments</strong> to start collecting.
                  </Alert>
                )}
                <Controller
                  name="fee_description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Fee Notes"
                      fullWidth
                      size="small"
                      placeholder="e.g. Refundable if no damage, check payable to HOA"
                      error={Boolean(errors.fee_description)}
                      helperText={errors.fee_description?.message}
                    />
                  )}
                />
              </Stack>
            </Box>
          </Box>

          <Box sx={sectionSx}>
            <Box sx={sectionHeaderSx}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Guest Policy</Typography>
            </Box>
            <Box sx={sectionBodySx}>
              <Stack spacing={2.5}>
                <Grid container spacing={2}>
                  <Grid xs={12} sm={6}>
                    <Controller
                      name="max_guests"
                      control={control}
                      render={({ field: { value, onChange, ...field } }) => (
                        <TextField
                          {...field}
                          value={value ?? ''}
                          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
                          label="Max Guests"
                          type="number"
                          fullWidth
                          size="small"
                          InputProps={{
                            endAdornment: (
                              <Tooltip title="Enforced per state fire/safety codes">
                                <InputAdornment position="end">
                                  <InfoIcon size={16} style={{ cursor: 'help', opacity: 0.5 }} />
                                </InputAdornment>
                              </Tooltip>
                            ),
                          }}
                          error={Boolean(errors.max_guests)}
                          helperText={errors.max_guests?.message ?? 'Leave blank for no limit'}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <Stack direction="row" spacing={4}>
                  <Controller
                    name="guest_count_required"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <FormControlLabel
                        control={<Switch size="small" checked={value} onChange={(e) => onChange(e.target.checked)} />}
                        label={
                          <Stack spacing={0}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>Guest count required</Typography>
                            <Typography variant="caption" color="text.secondary">Residents must enter guest count</Typography>
                          </Stack>
                        }
                      />
                    )}
                  />
                  <Controller
                    name="alcohol_question_enabled"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <FormControlLabel
                        control={<Switch size="small" checked={value} onChange={(e) => onChange(e.target.checked)} />}
                        label={
                          <Stack spacing={0}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>Alcohol question</Typography>
                            <Typography variant="caption" color="text.secondary">Ask if alcohol will be served</Typography>
                          </Stack>
                        }
                      />
                    )}
                  />
                </Stack>
              </Stack>
            </Box>
          </Box>

          <Box sx={sectionSx}>
            <Box
              sx={{
                ...sectionHeaderSx,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Required Documents</Typography>
                <Typography variant="caption" color="text.secondary">Residents upload these when paying the deposit</Typography>
              </Box>
              <Button
                type="button"
                size="small"
                startIcon={<PlusIcon size={14} />}
                onClick={() => append('')}
                variant="outlined"
                sx={{ fontWeight: 500 }}
              >
                Add
              </Button>
            </Box>
            <Box sx={sectionBodySx}>
              {fields.length === 0 ? (
                <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 2 }}>
                  No required documents
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {fields.map((field, index) => (
                    <Box key={field.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Controller
                        name={`required_documents.${index}`}
                        control={control}
                        render={({ field: f }) => (
                          <TextField
                            {...f}
                            fullWidth
                            size="small"
                            placeholder="e.g. Photo ID, Insurance certificate, Signed agreement"
                            error={Boolean(errors.required_documents?.[index])}
                          />
                        )}
                      />
                      <IconButton type="button" size="small" onClick={() => remove(index)} aria-label="Remove" sx={{ mt: 0.25 }}>
                        <MinusIcon size={16} />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Box>
        </Stack>
      )}

      {/* ─── Settings (edit only) ─── */}
      {tab === 3 && mode === 'edit' && (
        <Stack spacing={3}>
          <Box sx={sectionSx}>
            <Box sx={sectionHeaderSx}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Reservation Settings</Typography>
            </Box>
            <Box sx={sectionBodySx}>
              <Stack spacing={2}>
                <Controller
                  name="requires_approval"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1.5,
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Requires Approval</Typography>
                        <Typography variant="caption" color="text.secondary">
                          A manager must approve reservations before they are confirmed
                        </Typography>
                      </Box>
                      <Switch size="small" checked={value ?? true} onChange={(e) => onChange(e.target.checked)} />
                    </Box>
                  )}
                />
                <Controller
                  name="is_active"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        border: '1px solid',
                        borderColor: value ? 'success.main' : 'divider',
                        bgcolor: value ? 'action.selected' : 'transparent',
                        borderRadius: 1.5,
                        transition: 'all 0.15s',
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Active
                          {value && (
                            <Chip label="Visible to residents" size="small" color="success" variant="outlined" sx={{ ml: 1, height: 20, fontSize: '0.6875rem' }} />
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Inactive amenities are hidden from residents
                        </Typography>
                      </Box>
                      <Switch size="small" checked={value ?? true} onChange={(e) => onChange(e.target.checked)} />
                    </Box>
                  )}
                />
              </Stack>
            </Box>
          </Box>
        </Stack>
      )}
    </Box>
  );
}

/** Build default form values for creating a new amenity */
export function buildCreateDefaults(): AmenityFormValues {
  const availability = {} as AmenityFormValues['availability'];
  for (const day of DAYS_OF_WEEK) {
    availability[day] = { enabled: false, start: DEFAULT_START, end: DEFAULT_END };
  }
  return {
    name: '',
    description: '',
    rules: '',
    fee_description: '',
    deposit_amount: undefined,
    total_fee_amount: undefined,
    required_documents: [],
    availability,
    seasonal_enabled: false,
    seasonal_start_date: null,
    seasonal_end_date: null,
    time_block_duration: null,
    reservation_min_duration: null,
    reservation_max_duration: null,
    max_guests: undefined,
    guest_count_required: true,
    alcohol_question_enabled: false,
    requires_approval: true,
    is_active: true,
  };
}

/** Populate form values from an existing Amenity record */
export function buildEditDefaults(amenity: {
  name: string;
  description?: string;
  rules?: string;
  fee_description?: string;
  deposit_amount?: number | string | null;
  total_fee_amount?: number | string | null;
  required_documents?: string[];
  availability_schedule?: Record<string, { start: string; end: string }> | null;
  seasonal_start_date?: string | null;
  seasonal_end_date?: string | null;
  time_block_duration?: number | null;
  reservation_min_duration?: number | null;
  reservation_max_duration?: number | null;
  max_guests?: number | null;
  guest_count_required?: boolean;
  alcohol_question_enabled?: boolean;
  requires_approval?: boolean;
  is_active?: boolean;
}): AmenityFormValues {
  const availability = {} as AmenityFormValues['availability'];
  for (const day of DAYS_OF_WEEK) {
    const entry = amenity.availability_schedule?.[day];
    availability[day] = entry
      ? { enabled: true, start: entry.start, end: entry.end }
      : { enabled: false, start: DEFAULT_START, end: DEFAULT_END };
  }

  return {
    name: amenity.name,
    description: amenity.description ?? '',
    rules: amenity.rules ?? '',
    fee_description: amenity.fee_description ?? '',
    deposit_amount: amenity.deposit_amount != null ? Number(amenity.deposit_amount) : undefined,
    total_fee_amount: amenity.total_fee_amount != null ? Number(amenity.total_fee_amount) : undefined,
    required_documents: Array.isArray(amenity.required_documents) ? [...amenity.required_documents] : [],
    availability,
    seasonal_enabled: !!(amenity.seasonal_start_date || amenity.seasonal_end_date),
    seasonal_start_date: amenity.seasonal_start_date ?? null,
    seasonal_end_date: amenity.seasonal_end_date ?? null,
    time_block_duration: amenity.time_block_duration ?? null,
    reservation_min_duration: amenity.reservation_min_duration ?? null,
    reservation_max_duration: amenity.reservation_max_duration ?? null,
    max_guests: amenity.max_guests ?? null,
    guest_count_required: amenity.guest_count_required ?? true,
    alcohol_question_enabled: amenity.alcohol_question_enabled ?? false,
    requires_approval: amenity.requires_approval ?? true,
    is_active: amenity.is_active ?? true,
  };
}
